import React, { useRef, useState } from "react";
import { Input } from "./ui/input";
import { X, Move, MapPin } from "lucide-react";

const numInput =
  "h-8 px-2 border border-[#A8C97D] rounded-none text-sm font-mono-data focus:border-[#5E8A1E] focus:outline-none w-full";
const lbl = "text-[10px] uppercase tracking-wide text-zinc-500";

export default function PlacePanel({ config, categories, onChange, onClose }) {
  const initX = typeof window !== "undefined" ? window.innerWidth - 360 : 600;
  const [pos, setPos] = useState({ x: initX, y: 90 });
  const drag = useRef(null);

  const onHeaderDown = (e) => {
    drag.current = { sx: e.clientX, sy: e.clientY, ox: pos.x, oy: pos.y };
    const move = (ev) =>
      setPos({
        x: drag.current.ox + (ev.clientX - drag.current.sx),
        y: drag.current.oy + (ev.clientY - drag.current.sy),
      });
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const set = (patch) => onChange({ ...config, ...patch });

  return (
    <div
      data-testid="place-panel"
      style={{ position: "fixed", left: pos.x, top: pos.y, width: 320, zIndex: 50 }}
      className="bg-white border-2 border-[#5E8A1E] shadow-2xl"
    >
      <div
        onPointerDown={onHeaderDown}
        data-testid="place-panel-header"
        className="flex items-center justify-between px-3 h-10 bg-[#5E8A1E] text-white cursor-move select-none"
      >
        <span className="flex items-center gap-2 font-head text-sm font-bold">
          <Move size={14} strokeWidth={2} /> Amplasează element
        </span>
        <button onClick={onClose} data-testid="place-panel-close" className="hover:opacity-70">
          <X size={16} strokeWidth={2} />
        </button>
      </div>

      <div className="p-3 space-y-3">
        <label className="flex flex-col gap-0.5">
          <span className={lbl}>Categorie (tip)</span>
          <select
            data-testid="place-category"
            value={config.category_id}
            onChange={(e) => {
              const cat = categories.find((c) => c.id === e.target.value);
              set({
                category_id: e.target.value,
                length: cat?.length ?? config.length,
                width: cat?.width ?? config.width,
              });
            }}
            className="h-9 px-2 border border-[#A8C97D] rounded-none text-sm bg-white focus:border-[#5E8A1E] focus:outline-none"
          >
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-0.5">
          <span className={lbl}>Nume</span>
          <Input
            data-testid="place-name"
            value={config.name}
            onChange={(e) => set({ name: e.target.value })}
            className="h-8 rounded-none border-[#A8C97D] text-sm"
          />
        </label>

        <div className="grid grid-cols-2 gap-2">
          <label className="flex flex-col gap-0.5">
            <span className={lbl}>Lungime (m)</span>
            <input data-testid="place-length" type="number" step="0.1" value={config.length} className={numInput}
              onChange={(e) => set({ length: parseFloat(e.target.value) || 0 })} />
          </label>
          <label className="flex flex-col gap-0.5">
            <span className={lbl}>Lățime (m)</span>
            <input data-testid="place-width" type="number" step="0.1" value={config.width} className={numInput}
              onChange={(e) => set({ width: parseFloat(e.target.value) || 0 })} />
          </label>
        </div>

        <label className="flex flex-col gap-0.5">
          <span className={lbl}>Consum electric (kW)</span>
          <input data-testid="place-consumption" type="number" step="0.1" value={config.consumption} className={numInput}
            onChange={(e) => set({ consumption: parseFloat(e.target.value) || 0 })} />
        </label>

        <label className="flex flex-col gap-0.5">
          <span className={lbl}>Consum noaptea (kW)</span>
          <input data-testid="place-consumption-night" type="number" step="0.1" value={config.consumption_night ?? 0} className={numInput}
            onChange={(e) => set({ consumption_night: parseFloat(e.target.value) || 0 })} />
        </label>

        <label className="flex flex-col gap-0.5">
          <span className={lbl}>Unghi (rotație): {config.rotation}°</span>
          <input data-testid="place-rotation" type="range" min="0" max="359" step="1" value={config.rotation}
            onChange={(e) => set({ rotation: parseInt(e.target.value) })} className="accent-[#5E8A1E]" />
        </label>

        <label className="flex flex-col gap-0.5">
          <span className={lbl}>Mobilier</span>
          <textarea data-testid="place-mobilier" rows={2} value={config.mobilier || ""}
            onChange={(e) => set({ mobilier: e.target.value })}
            className="px-2 py-1 border border-[#A8C97D] rounded-none text-sm focus:border-[#5E8A1E] focus:outline-none resize-none" />
        </label>
        <label className="flex flex-col gap-0.5">
          <span className={lbl}>Branding</span>
          <textarea data-testid="place-branding" rows={2} value={config.branding || ""}
            onChange={(e) => set({ branding: e.target.value })}
            className="px-2 py-1 border border-[#A8C97D] rounded-none text-sm focus:border-[#5E8A1E] focus:outline-none resize-none" />
        </label>
        <label className="flex flex-col gap-0.5">
          <span className={lbl}>Altele</span>
          <textarea data-testid="place-altele" rows={2} value={config.altele || ""}
            onChange={(e) => set({ altele: e.target.value })}
            className="px-2 py-1 border border-[#A8C97D] rounded-none text-sm focus:border-[#5E8A1E] focus:outline-none resize-none" />
        </label>

        <div className="flex items-center gap-2 text-xs text-[#5E8A1E] bg-[#5E8A1E]/10 px-2 py-2 font-medium">
          <MapPin size={14} strokeWidth={2} /> Click pe hartă pentru a amplasa
        </div>
      </div>
    </div>
  );
}
